import { Location } from 'react-router-dom';

import auth0 from 'auth0-js';
import jwt_decode from 'jwt-decode';

import { Auth0ParsedHash } from '../types/types.custom';
import '../types/types.custom';

import { SESSION_ACCESS_TOKEN, SESSION_EXPIRES_AT, SESSION_USER_EMAIL } from '../constants/localStorage';
import { baseWindowUrl } from '../constants/window';

import { ENV_VARS_WRAPPER } from '../configs/envVariables';
import history from '../historyUtil';
import { ReturnLocation, toSafeReturnLocation } from './redirect';
import { beforeFirstAuthentication, getSessionExpirationTimestamp, removeSessionStorage, setIsIstaUser } from './utils';

const AUTH_REDIRECT_STATE_KEY = 'auth_redirect_state';

type LoginOptions = { signup?: boolean; customQueryParams?: { t: 'ista' } | null };

class Auth {
  constructor() {
    this.scheduleRenewal();
  }

  tokenRenewalTimeout = null;
  auth0 = new auth0.WebAuth({
    domain: window[ENV_VARS_WRAPPER]?.REACT_APP_AUTH0_DOMAIN || '',
    clientID: window[ENV_VARS_WRAPPER]?.REACT_APP_AUTH0_CLIENT_ID || '',
    redirectUri: `${baseWindowUrl()}/callback`,
    audience: window[ENV_VARS_WRAPPER]?.REACT_APP_AUTH0_AUDIENCE || 'CoreGraphQLAPIdev',
    responseType: 'token id_token', // Include 'id_token' for nonce handling
    scope: 'openid email profile',
  });
  login = ({ signup = false, customQueryParams = null }: LoginOptions) => {
    removeSessionStorage();

    if (signup) {
      this.auth0.authorize({
        screen_hint: 'signup',
        page: 'signup',
        responseType: 'token id_token',
        ...customQueryParams,
      });
    } else {
      this.saveRedirectState(history.location);
      this.auth0.authorize({ ...customQueryParams });
    }
  };
  handleAuthentication = (callback): void => {
    this.auth0.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken) {
        this.setSession(authResult);
        callback();
      } else if (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        this.logout();
      }
    });
  };
  setSession = (authResult: Auth0ParsedHash): void => {
    const decodedToken = jwt_decode(authResult.accessToken);
    const expiresAt = (authResult.expiresIn * 1000 + Date.now()).toString();
    const email = decodedToken['http://nexoya.io/email'];
    localStorage.setItem(SESSION_ACCESS_TOKEN, authResult.accessToken);
    localStorage.setItem(SESSION_EXPIRES_AT, expiresAt);
    localStorage.setItem(SESSION_USER_EMAIL, email);
    setIsIstaUser(email);
    this.scheduleRenewal();
  };
  getAccessToken = (): string => {
    return localStorage.getItem(SESSION_ACCESS_TOKEN) || '';
  };
  logout = (): void => {
    if (this.tokenRenewalTimeout) {
      clearTimeout(this.tokenRenewalTimeout);
    }

    removeSessionStorage();

    // Perform logout with the configured options
    this.auth0.logout({
      clientID: window[ENV_VARS_WRAPPER]?.REACT_APP_AUTH0_CLIENT_ID || '',
    });
  };
  isAuthenticated = (): boolean => {
    return Date.now() < getSessionExpirationTimestamp();
  };
  restoreSession = (): Promise<boolean> => Promise.resolve(this.isAuthenticated());
  saveRedirectState = (state: Location) => {
    localStorage.setItem(AUTH_REDIRECT_STATE_KEY, JSON.stringify(state));
  };
  getAndClearRedirectState = (): ReturnLocation | null => {
    const state = localStorage.getItem(AUTH_REDIRECT_STATE_KEY);
    localStorage.removeItem(AUTH_REDIRECT_STATE_KEY);
    if (!state) return null;
    try {
      return toSafeReturnLocation(JSON.parse(state), window.location.origin);
    } catch {
      return null;
    }
  };
  renewToken = (): void => {
    this.auth0.checkSession(
      {
        redirectUri: `${baseWindowUrl()}/callback`,
      },
      (err, result) => {
        if (result && result.accessToken) {
          this.setSession(result);
        } else if (err) {
          // eslint-disable-next-line no-console
          console.log(err);
          this.logout();
        }
      },
    );
  };
  scheduleRenewal = (): void => {
    const delay = getSessionExpirationTimestamp() - Date.now();

    // The user has not been authenticated in this browser yet. Wait until we
    // Auth0.authorize the user for the first time before we schedule renewal
    if (beforeFirstAuthentication(delay)) {
      return;
    }

    // The user most likely refreshed the browser.
    // We have expiration timestamp, so let's schedule renewal
    if (delay > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewToken();
      }, delay);
      return;
    }

    // The expiration timestamp has expired. Let's renew the token.
    this.renewToken();
  };
}

const auth = new Auth();
export default auth;
